import { useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DataState, CardListSkeleton } from '@/shared/ui';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAuth } from '@context/authentication';
import { getCurrentUserRole, isOperationalOrHigher, isAdmin } from '@/shared/utils/permissions';
import {
  launchActionErrorKey,
} from '@/shared/utils/launch-reservation-gate';
import { LaunchesService } from '../services/launchesApi';
import { useLaunchBoard } from '../hooks/useLaunchBoard';
import { useLaunchActions } from '../hooks/useLaunchActions';
import { LaunchBoardHeader } from '../components/LaunchBoardHeader';
import { LaunchBoardGrid } from '../components/LaunchBoardGrid';
import { LaunchBoardMobileList } from '../components/LaunchBoardMobileList';
import { LaunchBoardDesktopMirror } from '../components/LaunchBoardDesktopMirror';
import { LaunchMaterialsTab } from '../components/materials/LaunchMaterialsTab';
import { LaunchInlineConfirm } from '../components/LaunchInlineConfirm';
import { LaunchReservationNotice } from '../components/LaunchReservationNotice';
import { formatUnitPrice } from '../components/LaunchUnitCard';
import type { Launch } from '../types';
import { LaunchUnitActionPanel } from '../components/LaunchUnitActionPanel';
import { LaunchBoardDashboard } from '../components/LaunchBoardDashboard';
import { LaunchConfirmDialog, LaunchRevertSoldDialog } from '../components/LaunchConfirmDialog';
import { EditLaunchDrawer } from '../components/edit-launch';
import { MobileBottomSheet } from '@/shared/ui/components/composed/mobile-sheet';
import { useIsMobile } from '@/shared/hooks';
import '../styles/launch-tokens.css';

type Translate = (key: string) => string;

/**
 * Runs a board mutation, then reloads or toasts the mapped API error.
 */
function runBoardMutation(
  task: Promise<unknown>,
  retry: () => void,
  t: Translate,
): void {
  task.then(retry).catch((err) => toast.error(t(launchActionErrorKey(err))));
}

/**
 * Opens a draft launch so units can accept reservations.
 */
function activateDraftLaunch(
  agencyId: string,
  launchId: string,
  retry: () => void,
  t: Translate,
): void {
  LaunchesService.open(agencyId, launchId)
    .then(() => toast.success(t('list.publishSuccess')))
    .then(retry)
    .catch((err) => toast.error(t(launchActionErrorKey(err))));
}

/**
 * Board view for a single launch following the Housien detail page pattern.
 */
export function LaunchBoardPage() {
  const { t } = useTranslation('features/launches');
  const { agency_id: agencyId = '', launch_id: launchId = '' } = useParams<{
    agency_id: string;
    launch_id: string;
  }>();
  const { agency } = useAgency((s) => s);
  const { user } = useAuth();
  const currentUserEmail = user?.profile?.email;
  const currentUserRole = getCurrentUserRole(agency, currentUserEmail);
  const canManage = isOperationalOrHigher(currentUserRole);
  const canRevertSale = isAdmin(currentUserRole);
  const { board, isLoading, isError, retry, selectedUnit, selectUnit } =
    useLaunchBoard(agencyId, launchId);
  const actions = useLaunchActions(agencyId, launchId);
  const [pendingSelfRelease, setPendingSelfRelease] = useState<string | null>(null);
  const [pendingRevertSold, setPendingRevertSold] = useState(false);
  const [editingLaunch, setEditingLaunch] = useState<Launch | null>(null);
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<'units' | 'materials' | 'dashboard'>('units');

  const refreshEditingLaunch = () => {
    LaunchesService.get(agencyId, launchId)
      .then(setEditingLaunch)
      .catch((err) => toast.error(t(launchActionErrorKey(err))));
  };

  const openEdit = () => refreshEditingLaunch();

  const handleUnitsChanged = () => {
    refreshEditingLaunch();
    retry();
  };

  return (
    // min-h-0 is what makes overflow-y-auto actually scroll here: a flex
    // child defaults to min-height:auto, refuses to shrink below its
    // content, and the overflow never engages — so the app shell clips it
    // instead. The shell's root is 48px shorter than the viewport (the
    // cookie banner sits in the flow), and without this the bottom of the
    // board was simply unreachable.
    <div className="launch-scope flex h-full min-h-0 flex-col overflow-y-auto bg-(--surface-primary) p-[var(--space-5)]">
      <LaunchBoardHeader
        title={board?.name ?? ''}
        status={board?.status}
        canEdit={canManage && board?.status !== 'closed'}
        onEdit={openEdit}
        canActivate={canManage && board?.status === 'draft'}
        onActivate={() => activateDraftLaunch(agencyId, launchId, retry, t)}
      />
      <LaunchReservationNotice status={board?.status} />

      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={board !== null && board.units.length === 0 && !isLoading}
        onRetry={retry}
        loadingFallback={<CardListSkeleton count={8} />}
      >
        {board && (
          <>
            {/* No separate legend on either surface: the status chips in
                the board itself explain the colours and filter by them at
                the same time. Keeping LaunchBoardLegend here printed the
                same four labels twice, with the tab bar wedged between
                them. */}
            <div
              role="tablist"
              // The removed legend used to carry my-4; the tab bar now owns
              // its own breathing room instead of inheriting someone else's.
              className="mt-[var(--space-4)] mb-[var(--space-4)] flex gap-[var(--space-2)] border-b border-[var(--border-subtle)]"
            >
              {(['units', 'materials', 'dashboard'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  // 40px and 15px match the prototype on desktop; the phone
                  // keeps 48px because the tab is also a touch target.
                  className="min-h-[var(--space-12)] px-[var(--space-3)] text-[length:var(--text-body)] lg:min-h-[var(--space-10)]"
                  style={{
                    color: tab === key ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    borderBottom: tab === key
                      ? '2px solid var(--seed-base)'
                      : '2px solid transparent',
                  }}
                >
                  {t(`board.tabs.${key}`)}
                </button>
              ))}
            </div>

            {tab === 'materials' && (
              <LaunchMaterialsTab
                agencyId={agencyId}
                launchId={launchId}
                canManage={canManage}
              />
            )}

            {/* The Painel tab renders on every width. It used to be gated
                on isMobile, with the desktop copy living inside the units
                grid — which is display:none on this tab, so the tab was
                simply empty on desktop. */}
            {tab === 'dashboard' && (
              <LaunchBoardDashboard agencyId={agencyId} launchId={launchId} />
            )}

            <div
              // xl, not md: a 340px rail beside the board needs real width. At
              // 768-1279px it was eating half the screen and squeezing the
              // units into a column narrower than the rail itself.
              className="grid flex-1 grid-cols-1 gap-[var(--space-4)] xl:grid-cols-[1fr_var(--launch-rail-width)]"
              style={{ display: tab === 'units' ? undefined : 'none' }}
            >
              <div className="min-w-0">
                {isMobile ? (
                  <LaunchBoardMobileList
                    units={board.units}
                    currentUserEmail={currentUserEmail}
                    onSelectUnit={selectUnit}
                  />
                ) : (
                  <LaunchBoardDesktopMirror
                    units={board.units}
                    selectedUnitId={selectedUnit?.unit_id ?? null}
                    onSelectUnit={selectUnit}
                  />
                )}
              </div>

              <aside className="flex min-w-0 flex-col gap-[var(--space-4)]">
                {selectedUnit && !isMobile && (
                  <LaunchUnitActionPanel
                    unit={selectedUnit}
                    agencyId={agencyId}
                    loading={actions.loading}
                    launchStatus={board.status}
                    canManage={canManage}
                    canRevertSale={canRevertSale}
                    currentUserEmail={currentUserEmail}
                    onHold={(uid, cid) => runBoardMutation(actions.hold(uid, cid), retry, t)}
                    onSelfRelease={(uid) => setPendingSelfRelease(uid)}
                    onConfirmDeposit={(uid) => runBoardMutation(actions.confirmDeposit(uid), retry, t)}
                    onMarkSold={(uid) => runBoardMutation(actions.markSold(uid), retry, t)}
                    onForceRelease={(uid) => runBoardMutation(actions.forceRelease(uid), retry, t)}
                    onRevertSold={() => setPendingRevertSold(true)}
                    onValidateDeposit={(uid) => runBoardMutation(actions.validateDeposit(uid), retry, t)}
                    onRejectDeposit={(uid) => runBoardMutation(actions.rejectDeposit(uid, ''), retry, t)}
                    onClose={() => selectUnit(null)}
                  />
                )}

                {/* Rail copy: only alongside the units, and only when the
                    Painel tab is not already showing it. */}
                {!isMobile && tab === 'units' && (
                  <LaunchBoardDashboard agencyId={agencyId} launchId={launchId} layout="rail" />
                )}
              </aside>
            </div>

            {/* On the phone the unit opens as a sheet over the board, not as
                a panel rendered under the whole list — where tapping a unit
                looked like nothing had happened. */}
            <MobileBottomSheet
              open={isMobile && selectedUnit !== null}
              title={selectedUnit ? t('board.unitTitle', { label: selectedUnit.label }) : ''}
              subtitle={selectedUnit?.tower ? `${selectedUnit.tower} · ${selectedUnit.floor ?? ''}` : undefined}
              onClose={() => selectUnit(null)}
              testId="launch-unit-sheet"
            >
              {selectedUnit && pendingSelfRelease !== null && (
                <LaunchInlineConfirm
                  title={t('confirm.selfReleaseTitle')}
                  description={t('confirm.selfReleaseDescription', { label: selectedUnit.label })}
                  confirmLabel={t('confirm.selfReleaseConfirm')}
                  destructive={false}
                  onCancel={() => setPendingSelfRelease(null)}
                  onConfirm={() => {
                    runBoardMutation(actions.selfRelease(pendingSelfRelease), retry, t);
                    setPendingSelfRelease(null);
                  }}
                />
              )}

              {selectedUnit && pendingRevertSold && (
                <LaunchInlineConfirm
                  title={t('confirm.revertSoldTitle')}
                  description={t('confirm.revertSoldDescription', { label: selectedUnit.label })}
                  confirmLabel={t('confirm.revertSoldConfirm')}
                  confirmWord="REVERTER"
                  onCancel={() => setPendingRevertSold(false)}
                  onConfirm={() => {
                    runBoardMutation(actions.revertSold(selectedUnit.unit_id), retry, t);
                    setPendingRevertSold(false);
                  }}
                />
              )}

              {selectedUnit && pendingSelfRelease === null && !pendingRevertSold && (
                <LaunchUnitActionPanel
                  unit={selectedUnit}
                  agencyId={agencyId}
                  loading={actions.loading}
                  launchStatus={board.status}
                  canManage={canManage}
                  canRevertSale={canRevertSale}
                  currentUserEmail={currentUserEmail}
                  onHold={(uid, cid) => runBoardMutation(actions.hold(uid, cid), retry, t)}
                  onSelfRelease={(uid) => setPendingSelfRelease(uid)}
                  onConfirmDeposit={(uid) => runBoardMutation(actions.confirmDeposit(uid), retry, t)}
                  onMarkSold={(uid) => runBoardMutation(actions.markSold(uid), retry, t)}
                  onForceRelease={(uid) => runBoardMutation(actions.forceRelease(uid), retry, t)}
                  onRevertSold={() => setPendingRevertSold(true)}
                  onValidateDeposit={(uid) => runBoardMutation(actions.validateDeposit(uid), retry, t)}
                  onRejectDeposit={(uid) => runBoardMutation(actions.rejectDeposit(uid, ''), retry, t)}
                  onClose={() => selectUnit(null)}
                  embedded
                />
              )}
            </MobileBottomSheet>
          </>
        )}
      </DataState>

      <LaunchConfirmDialog
        open={!isMobile && pendingSelfRelease !== null}
        title={t('confirm.selfReleaseTitle')}
        description={
          pendingSelfRelease
            ? t('confirm.selfReleaseDescription', {
                label: board?.units.find((u) => u.unit_id === pendingSelfRelease)?.label ?? '',
              })
            : ''
        }
        confirmLabel={t('confirm.selfReleaseConfirm')}
        cancelLabel={t('confirm.cancel')}
        destructive={false}
        onOpenChange={(open) => { if (!open) setPendingSelfRelease(null); }}
        onConfirm={() => {
          if (pendingSelfRelease) runBoardMutation(actions.selfRelease(pendingSelfRelease), retry, t);
          setPendingSelfRelease(null);
        }}
      />

      {selectedUnit && (
        <LaunchRevertSoldDialog
          open={!isMobile && pendingRevertSold}
          unitLabel={selectedUnit.label}
          clientLabel={selectedUnit.held_for_client_id}
          brokerLabel={selectedUnit.held_by_broker_id}
          priceLabel={formatUnitPrice(selectedUnit.list_price) || undefined}
          onOpenChange={setPendingRevertSold}
          onConfirm={() => {
            runBoardMutation(actions.revertSold(selectedUnit.unit_id), retry, t);
            setPendingRevertSold(false);
          }}
        />
      )}

      <EditLaunchDrawer
        agencyId={agencyId}
        launch={editingLaunch}
        open={editingLaunch !== null}
        onClose={() => setEditingLaunch(null)}
        onUpdated={retry}
        onUnitsChanged={handleUnitsChanged}
      />
    </div>
  );
}
